import { collection, query, where, orderBy, limit, startAfter, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, QueryDocumentSnapshot, DocumentData, Timestamp } from 'firebase/firestore';
import { db } from 'src/firebase';
import { Promoter } from './types/promoter.entity';
import { FilterQuery } from 'src/firebase/firestore/types';

class PromoterRepository {
  private collectionName = 'promoters';

  async find(options: FilterQuery<Promoter> = {}): Promise<Promoter[]> {
    try {
      let q = query(collection(db, this.collectionName));

      // Apply where conditions
      if (options.where) {
        Object.entries(options.where).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            q = query(q, where(key, '==', value));
          }
        });
      }

      // Apply orderBy
      if (options.orderBy) {
        if (Array.isArray(options.orderBy)) {
          // Handle array of orderBy items
          options.orderBy.forEach((orderByItem) => {
            if (Array.isArray(orderByItem) && orderByItem.length === 2) {
              const field = orderByItem[0] as string;
              const direction = orderByItem[1] as "asc" | "desc";
              q = query(q, orderBy(field, direction));
            }
          });
        } else {
          // Handle single orderBy item
          const field = options.orderBy[0] as string;
          const direction = options.orderBy[1] as "asc" | "desc";
          q = query(q, orderBy(field, direction));
        }
      }

      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      // Apply offset (startAfter)
      if (options.offset) {
        const offsetDoc = await this.getOffsetDocument(options.offset);
        if (offsetDoc) {
          q = query(q, startAfter(offsetDoc));
        }
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Promoter[];
    } catch (error) {
      console.error('Error fetching promoters:', error);
      throw error;
    }
  }

  async findById(id: string): Promise<Promoter | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Promoter;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching promoter by ID:', error);
      throw error;
    }
  }

  // async delete(id: string): Promise<void> {
  //   try {
  //     const docRef = doc(db, this.collectionName, id);
  //     await deleteDoc(docRef);
  //   } catch (error) {
  //     console.error('Error deleting promoter:', error);
  //     throw error;
  //   }
  // }

  async count(where?: any): Promise<number> {
    try {
      let q = query(collection(db, this.collectionName));
      
      if (where) {
        Object.entries(where).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            q = query(q, where(key, '==', value));
          }
        });
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error counting promoters:', error);
      throw error;
    }
  }

  private async getOffsetDocument(offset: string): Promise<QueryDocumentSnapshot<DocumentData> | null> {
    try {
      const docRef = doc(db, this.collectionName, offset);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap : null;
    } catch (error) {
      console.error('Error getting offset document:', error);
      return null;
    }
  }
}

export const promoterRepository = new PromoterRepository();
